import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import type { DP } from '../../../drupal/blockDefinitions'
import {
  dummyData,
  randomCaption,
  randomTitle,
  randomWords, randomWysiwygText
} from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import type { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { expandableBlockClasses, ExpandableLayout } from './expandableBlock.layout'
import type { ExpandableBlockProps } from './expandableBlock.state'
import { parseExpandable } from '../../../utils/expandable'

export const expandableBlock: ModernBlock<ExpandableBlockProps, any, DP.ExpandableContent> =
  {
    kind: 'expandableBlock',
    render: (setRootState, rootState, blockIndex, blockProps) =>
      fromJSX(() => {
        return (
          <ExpandableLayout.Inner>
            <ExpandableLayout.Content
              label={blockProps.label}
              title={blockProps.title}
              intro={blockProps.intro}
              showPrimary={blockProps.showPrimary}
              showSecondary={blockProps.showSecondary}
              primaryAction={blockProps.primaryAction!}
              secondaryAction={blockProps.secondaryAction!}
              items={blockProps.expandableItems}
              rootState={rootState}
              setRootState={setRootState}
            />
          </ExpandableLayout.Inner>
        )
      }),
    blockClassName: getClassNameModern(expandableBlockClasses, 'block', 'mb-6'),
    inView: {
      showAs: 'section',
      threshold: 0.1,
      triggerOnce: true,
      inViewClassName: getClassNameModern(expandableBlockClasses, 'inview', 'inview'),
      updateState: (currentInviewState, currentBlockState) => {
        return {
          ...currentBlockState,
          inviewState: currentInviewState,
        }
      },
    },
    adapters: {
      drupal: (dpBlock: DP.ExpandableContent, blockIndex) => {
        const targetId = dpBlock.target.type[0].target_id
        if (targetId != 'expandable_content_blocks') return undefined
        return {
          anchorTitle: dpBlock.target.field_title[0]
            ? slugify(dpBlock.target.field_title[0]?.value)
            : `${targetId}-title-${blockIndex}`,
          title: dpBlock.target.field_title[0]?.value || undefined,
          excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu?.[0]?.value,
          label: dpBlock.target.field_label[0]?.value,
          intro: dpBlock.target.field_introduction[0]?.value || undefined,
          showPrimary: dpBlock.target.field_links[0] ? true : false,
          showSecondary: dpBlock.target.field_links[1] ? true : false,
          primaryAction: linkWrapper({
            title: dpBlock.target?.field_links[0]?.title!,
            url: dpBlock.target?.field_links[0]?.processed_url!,
            anchor: dpBlock.target?.field_links[0]?.options?.attributes?.id!,
          }),
          secondaryAction: linkWrapper({
            title: dpBlock.target?.field_links[1]?.title!,
            url: dpBlock.target?.field_links[1]?.processed_url!,
            anchor: dpBlock.target?.field_links[1]?.options?.attributes?.id!,
          }),
          expandableItems: parseExpandable(dpBlock.target.field_expandable_content_blocks)
        }
      }
    },
    seoEvents: (props, blockIndex) =>
      [
        ...props.expandableItems?.map(
          (item, index) =>
            ({
              id: `expandable-item-${index}`,
              event: 'onClick',
              category: 'expandable',
              label: window.location.pathname,
              action: item.title,
            } as SeoEvent)
        ),
        props.primaryAction
          ? {
              id: `primary_button`,
              event: 'onClick',
              category: 'expandable',
              label: window.location.pathname,
              action: 'primary_button_' + props.primaryAction.title,
            }
          : undefined,
      ].filter((i) => i != undefined) as SeoEvent[],
    sampleExample: [
      {
        label: randomWords(),
        title: randomTitle(),
        intro: dummyData.intro,
        showPrimary: true,
        primaryAction: dummyData.primaryButton,
        showSecondary: true,
        secondaryAction: dummyData.secondaryButton,
        anchorTitle: 'expand-sample-1',
        expandableItems: [
          {
            isOpen: false,
            title: randomTitle(),
            body: randomWysiwygText(),
            nestedBlocks: [
              {
                kind: 'quoteNested',
                design: "default",
                text: dummyData.quote,
                author: randomCaption(),
                index: 'quote-1'
              }
            ],
            index: 0,
          },
          {
            isOpen: false,
            title: randomTitle(),
            body: randomWysiwygText(),
            nestedBlocks: [
              {
                kind: 'quoteNested',
                design: 'default',
                text: dummyData.quote,
                author: randomCaption(),
                index: 'quote-1' },
              {
                kind: 'textNested',
                body: randomWysiwygText(),
                index: 'text-1' },
              {
                index: 'media-1',
                kind: 'mediaNested',
                displayOption: 'content-width',
                type: 'video',
                source: 'YouTube',
                url: dummyData.video,
                caption: randomCaption(),
              },
              {
                kind: 'textNested',
                body: randomWysiwygText(),
                index: 'text-2'
              },
              {
                kind: 'linkListNested',
                items: [
                  {
                    key: 'll-i1',
                    subTitle: randomTitle(),
                    link: linkWrapper({ title: randomTitle(), url: 'https://www.hoppinger.com' })
                  },
                  {
                    key: 'll-i2',
                    subTitle: randomTitle(),
                    link: linkWrapper({ title: randomTitle(), url: 'https://www.viddadigital.com' })
                  },
                  {
                    key: 'll-i3',
                    subTitle: '',
                    link: linkWrapper({ title: randomTitle(), url: '/' })
                  }
                ],
                index: 'text-3'
              },
            ],
            index: 1,
          },
          {
            isOpen: false,
            title: randomTitle(),
            description: randomTitle(),
            body: randomWysiwygText(),
            nestedBlocks: [
              {
                index: 'media-2',
                kind: 'mediaNested',
                displayOption: 'content-width',
                originalSize: {
                  showinOriginalSize: false
                },
                type: 'image',
                image: dummyData.image,
                source: 'Unsplash',
                caption: randomCaption(),
              },
              {
                kind: 'textNested',
                body: randomWysiwygText(),
                index: 'text-4'
              },
            ],
            index: 2,
          },
        ],
      },
      {
        intro: dummyData.intro,
        showPrimary: false,
        showSecondary: false,
        anchorTitle: 'expand-sample-2',
        expandableItems: [
          {
            isOpen: false,
            title: randomTitle(),
            body: randomWysiwygText(),
            nestedBlocks: [],
            index: 0,
          },
          {
            isOpen: false,
            title: randomTitle(),
            body: randomWysiwygText(),
            nestedBlocks: [
              {
                kind: 'quoteNested',
                design: 'default',
                text: dummyData.quote,
                author: randomCaption(),
                index: 'quote-1' },
              {
                kind: 'textNested',
                body: randomWysiwygText(),
                index: 'text-1' },
              {
                index: 'media-1',
                kind: 'mediaNested',
                displayOption: 'content-width',
                type: 'video',
                source: 'YouTube',
                url: dummyData.video,
                caption: randomCaption(),
              },
              {
                kind: 'textNested',
                body: randomWysiwygText(),
                index: 'text-2'
              },
              {
                kind: 'linkListNested',
                items: [
                  {
                    key: 'll-i1',
                    subTitle: randomTitle(),
                    link: linkWrapper({ title: randomTitle(), url: 'https://www.hoppinger.com' })
                  },
                  {
                    key: 'll-i2',
                    subTitle: randomTitle(),
                    link: linkWrapper({ title: randomTitle(), url: 'https://www.viddadigital.com' })
                  },
                  {
                    key: 'll-i3',
                    subTitle: '',
                    link: linkWrapper({ title: randomTitle(), url: '/' })
                  }
                ],
                index: 'text-3'
              },
            ],
            index: 1,
          },
          {
            isOpen: false,
            title: randomTitle(),
            description: randomTitle(),
            body: randomWysiwygText(),
            nestedBlocks: [],
            index: 2,
          },
        ],
      }
    ],
  }

  const handleHashOpenExpand = (anchor: string) => {
    if(anchor == '') return false;

    if(window.location.hash.substring(1) == anchor) return true;

    return false;
  }
